import { Link, useLocation } from 'react-router-dom'
import { CheckCircle2, Mail, Clock, FileText, ArrowRight } from 'lucide-react'
import Button from '../components/ui/Button'
import SEO from '../components/SEO'

export default function QuoteSuccess() {
  const location = useLocation()
  const { quoteId, product, email } = location.state || {}

  if (!quoteId) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-8 pb-16">
        <div className="text-center">
          <h2 className="text-2xl font-display font-bold text-ink mb-4">
            No quote request found
          </h2>
          <Link to="/products">
            <Button variant="primary">Browse Products</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-8 pb-16">
      <SEO title="Quote Request Received" description="" path="/quote-success" noindex />
      <div className="max-w-2xl w-full">
        <div className="rounded border border-theme bg-surface p-8 md:p-12 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'color-mix(in srgb, var(--accent) 15%, transparent)' }}>
            <CheckCircle2 size={40} className="text-accent" />
          </div>

          <h1 className="text-3xl font-display font-bold text-ink mb-3">
            Quote Request Submitted!
          </h1>
          <p className="text-[var(--ink-soft)] mb-8">
            Thank you for your interest in <strong className="text-ink">{product}</strong>. We've received your quote
            request and will get back to you shortly.
          </p>

          {/* Quote Details */}
          <div className="rounded bg-sunk p-6 mb-8 text-left">
            <h3 className="font-bold text-ink mb-4 flex items-center gap-2">
              <FileText size={18} />
              Quote Request Details
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[var(--ink-soft)]">Reference ID</span>
                <span className="font-boxy font-medium text-ink">
                  #{quoteId}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--ink-soft)]">Product</span>
                <span className="font-medium text-ink">{product}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--ink-soft)]">Email</span>
                <span className="font-medium text-ink">{email}</span>
              </div>
            </div>
          </div>

          {/* What Happens Next */}
          <div className="rounded p-6 mb-8 text-left" style={{ background: 'color-mix(in srgb, var(--accent) 5%, transparent)', borderLeft: '4px solid var(--accent)' }}>
            <h3 className="font-bold text-ink mb-4 flex items-center gap-2">
              <Clock size={18} />
              What happens next?
            </h3>
            <div className="space-y-3">
              {[
                {
                  step: '1',
                  text: 'Our team will check current pricing and availability',
                },
                {
                  step: '2',
                  text: 'We will prepare a detailed quote with delivery timeline',
                },
                {
                  step: '3',
                  text: 'You will receive the quote via email within 24 hours',
                },
                {
                  step: '4',
                  text: 'Once approved, we will send a secure payment link',
                },
              ].map((s) => (
                <div key={s.step} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 text-white" style={{ background: 'var(--accent)' }}>
                    {s.step}
                  </div>
                  <p className="text-sm text-[var(--ink-soft)]">{s.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Email Confirmation */}
          <div className="flex items-center justify-center gap-2 text-sm text-[var(--ink-soft)] mb-8">
            <Mail size={16} />
            <span>A confirmation email has been sent to {email}</span>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/products">
              <Button variant="ghost" size="lg">
                Browse More Products
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="primary" size="lg">
                Contact Sales <ArrowRight size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
